import React, {useEffect, useState, useContext, useRef, useCallback, useMemo} from 'react';
import {Row, Col, Form, Button, Modal, InputGroup} from "react-bootstrap";
import TypeBar from "../components/TypeBar";
import ProductList from "../components/ProductList";
import {fetchCategories, fetchProducts, fetchStores, fetchSizes} from "../http/productAPI";
import Pages from "../components/Pages";
import { useLocation, useNavigate } from 'react-router-dom';
import { Context } from '../index';
import { FormControl } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';
import useModalCleanup from '../hooks/useModalCleanup';
import '../styles/Shop.css';
import FloatingCartButton from '../components/FloatingCartButton';

const Shop = observer(() => {
    const { user } = useContext(Context);
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const gender = searchParams.get('gender');
    const type = searchParams.get('type');

    const shopStateKey = useMemo(() => `shop_state:v2:${type || 'all'}:${gender || 'all'}`, [type, gender]);

    const readShopState = useCallback(() => {
        try {
            const raw = sessionStorage.getItem(shopStateKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object') return null;
            return parsed;
        } catch {
            return null;
        }
    }, [shopStateKey]);

    const [categories, setCategories] = useState([])
    const [stores, setStores] = useState([])
    const [products, setProducts] = useState([])
    const [totalCount, setTotalCount] = useState(0)
    const [page, setPage] = useState(() => {
        const st = readShopState();
        const p = Number(st?.page);
        return Number.isFinite(p) && p > 0 ? p : 1;
    })
    const [limit] = useState(16)
    const [allSizes, setAllSizes] = useState([]);
    const [selectedSizes, setSelectedSizes] = useState(() => {
        const st = readShopState();
        return Array.isArray(st?.selectedSizes) ? st.selectedSizes : [];
    });
    const [pendingSizes, setPendingSizes] = useState(() => {
        const st = readShopState();
        return Array.isArray(st?.pendingSizes) ? st.pendingSizes : (Array.isArray(st?.selectedSizes) ? st.selectedSizes : []);
    });
    const [showFilters, setShowFilters] = useState(false);
    const [sizeInput, setSizeInput] = useState('');
    const [showSizeSuggestions, setShowSizeSuggestions] = useState(false);
    const [filteredSizes, setFilteredSizes] = useState([]);
    const getInitialFilters = () => {
        const st = readShopState();
        return {
            category_id: st?.filters?.category_id || '',
            store_id: st?.filters?.store_id || ''
        };
    };
    const [filters, setFilters] = useState(getInitialFilters);
    const [search, setSearch] = useState(() => {
        const st = readShopState();
        return typeof st?.search === 'string' ? st.search : '';
    });
    // Начальные значения сортировки: по умолчанию "Новинки" (createdAt DESC)
    const [productSortBy, setProductSortBy] = useState(() => {
        const st = readShopState();
        return st?.productSortBy || 'createdAt';
    });
    const [productSortDirection, setProductSortDirection] = useState(() => {
        const st = readShopState();
        return st?.productSortDirection || 'desc';
    });
    // === FIX: Отслеживание предыдущего type для предотвращения ложных сбросов ===
    const prevTypeRef = useRef(type);

    // На первом рендере ещё нет данных, поэтому держим загрузку включенной,
    // чтобы не показывать "Товары не найдены" до первого ответа.
    const [loadingProducts, setLoadingProducts] = useState(true);
    // Флаг: хотя бы один успешный ответ от сервера уже был получен
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    // Защита от гонок запросов: более старые ответы не должны перетирать новые
    const lastRequestIdRef = useRef(0);
    // Кеш отфильтрованных/отсортированных строк — пагинация без повторного API
    const allFilteredRowsRef = useRef([]);

    useModalCleanup(showFilters);

    // Один раз при монтировании чистим старые query-параметры (?page=1&search=...&sort_by=...),
    // чтобы они больше не мозолили глаза и не путали историю.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const hadLegacy =
            params.has('page') ||
            params.has('search') ||
            params.has('sort_by') ||
            params.has('sort_dir') ||
            params.has('sizes') ||
            params.has('category_id') ||
            params.has('store_id');
        if (!hadLegacy) return;

        // Оставляем только type/gender, остальное выкидываем.
        const clean = new URLSearchParams();
        if (type) clean.set('type', type);
        if (gender) clean.set('gender', gender);
        navigate({ pathname: location.pathname, search: clean.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // выполняем только один раз при монтировании

    // Теперь просто:
    const allCategories = categories;

    // Сброс выбранной категории при смене типа (только если type действительно изменился)
    // === FIX: Сбрасываем категорию только при реальном изменении type, не при возврате ===
    useEffect(() => {
        // Сбрасываем категорию только если type действительно изменился
        if (prevTypeRef.current !== type && prevTypeRef.current !== null && prevTypeRef.current !== undefined) {
            // Проверяем, нет ли сохраненной категории в URL
            const params = new URLSearchParams(location.search);
            const categoryIdFromURL = params.get('category_id') || '';
            // Сбрасываем только если в URL нет сохраненной категории
            if (!categoryIdFromURL) {
                setFilters(prev => ({ ...prev, category_id: '' }));
            }
        }
    }, [type, location.search]);

    // Сброс фильтров при смене типа (только если type действительно изменился)
    // === FIX: Этот useEffect должен выполняться ПОСЛЕ восстановления из URL ===
    useEffect(() => {
        // === FIX: Сбрасываем размеры только если type действительно изменился ===
        // И только если в URL нет сохраненных размеров (чтобы не сбросить при возврате)
        if (prevTypeRef.current !== type && prevTypeRef.current !== null && prevTypeRef.current !== undefined) {
            const params = new URLSearchParams(location.search);
            const sizesParam = params.get('sizes');
            // Сбрасываем только если в URL нет сохраненных размеров
            if (!sizesParam) {
                setSelectedSizes([]);
                setPendingSizes([]);
                setSizeInput('');
                setFilteredSizes([]);
                setShowSizeSuggestions(false);
            }
        }
        prevTypeRef.current = type;
    }, [type, location.search]);

    // Загрузка размеров в зависимости от категории
    useEffect(() => {
        const loadSizes = async () => {
            if (type === 'shoe') {
                const data = await fetchSizes('shoe');
                setAllSizes(data.filter(sz => sz.type === 'shoe'));
            } else if (type === 'clothes' && filters.category_id) {
                const categoryObj = categories.find(c => String(c.id) === String(filters.category_id));
                const catName = categoryObj?.name;
                let data;
                if (catName === 'Бюстгальтер') {
                    data = await fetchSizes('clothes', 'Бюстгальтер');
                    setAllSizes(data.filter(sz => sz.category === 'Бюстгальтер'));
                } else if (catName === 'Трусы') {
                    data = await fetchSizes('clothes', 'Трусы');
                    setAllSizes(data.filter(sz => sz.category === 'Трусы'));
                } else if (catName === 'Детская одежда') {
                    data = await fetchSizes('clothes', 'Детская одежда');
                    setAllSizes(data.filter(sz => sz.category === 'Детская одежда'));
                } else {
                    data = await fetchSizes('clothes');
                    setAllSizes(data.filter(sz => !sz.category));
                }
            } else {
                setAllSizes([]);
            }
        };
        loadSizes();
    }, [type, filters.category_id, categories]);

    // Фильтрация размеров при вводе
    useEffect(() => {
        if (sizeInput.trim() === '') {
            setFilteredSizes(allSizes); // Показываем все размеры, если поле пустое
            return;
        }
        const filtered = allSizes.filter(size => 
            size.value.toLowerCase().includes(sizeInput.toLowerCase())
        );
        setFilteredSizes(filtered);
    }, [sizeInput, allSizes]);

    // Обновляем список размеров при изменении allSizes
    useEffect(() => {
        setFilteredSizes(allSizes);
    }, [allSizes]);

    useEffect(() => {
        fetchCategories().then(data => {
            setCategories(data);
        });
        fetchStores().then(data => setStores(data));
    }, []);

    // === УДАЛЕНО: Дублирующий useEffect, который сбрасывал размеры ===
    // Этот useEffect был удален, так как он дублировал логику выше и мог сбрасывать размеры при возврате

    // --- Сброс страницы при изменении поиска ---
    useEffect(() => {
        setPage(1);
    }, [search]);

    useEffect(() => {
        setPage(1);
    }, [filters.category_id, filters.store_id, selectedSizes, productSortBy, productSortDirection]);

    // Собираем множество id выбранной категории и всех её дочерних категорий
    const getCategoryWithChildrenIds = useCallback((rootId) => {
        const ids = new Set();
        if (!rootId) return ids;
        const rootStr = String(rootId);
        ids.add(rootStr);
        const queue = [rootStr];

        while (queue.length > 0) {
            const current = queue.shift();
            categories.forEach(cat => {
                if (cat.parent_id && String(cat.parent_id) === current) {
                    const childId = String(cat.id);
                    if (!ids.has(childId)) {
                        ids.add(childId);
                        queue.push(childId);
                    }
                }
            });
        }

        return ids;
    }, [categories]);

    const getProductQuantity = useCallback((product) => {
        if (product.type === 'toy') {
            return product.toy?.quantity ?? product.stock ?? 0;
        }
        if (product.type === 'shoe' || product.type === 'clothes') {
            return (product.variants || []).reduce((sum, v) => sum + (Number(v.quantity) || 0), 0);
        }
        return 0;
    }, []);

    const applyClientFilters = useCallback((rows, { storeId, categoryId, searchText }) => {
        let result = rows || [];

        if (searchText && searchText.trim() !== '') {
            const searchLower = searchText.trim().toLowerCase();
            result = result.filter(product => {
                const nameMatch = (product.name || '').toLowerCase().includes(searchLower);
                const articleMatch = (product.article || product.clothes?.article || '').toLowerCase().includes(searchLower);
                const categoryMatch = (product.category?.name || '').toLowerCase().includes(searchLower);
                const barcodeMatch = [
                    product.barcode,
                    product.toy?.barcode,
                    ...(Array.isArray(product.variants) ? product.variants.map(v => v.barcode) : []),
                ]
                    .filter(Boolean)
                    .map(v => String(v).toLowerCase())
                    .some(b => b.includes(searchLower));
                return nameMatch || articleMatch || categoryMatch || barcodeMatch;
            });
        }

        if (categoryId) {
            const idsSet = getCategoryWithChildrenIds(categoryId);
            result = result.filter(product => idsSet.has(String(product.category_id)));
        }

        if (storeId) {
            const storeIdStr = String(storeId);
            result = result.filter(product => {
                const hasStoreInLinks =
                    (Array.isArray(product.stores) && product.stores.some(s => String(s.id) === storeIdStr)) ||
                    (Array.isArray(product.product_stores) && product.product_stores.some(ps => String(ps.storeId) === storeIdStr));

                if (product.type === 'shoe' || product.type === 'clothes') {
                    const variants = Array.isArray(product.variants) ? product.variants : [];
                    const hasVariantInStore = variants.some(v => {
                        if (v.storeId !== undefined && v.storeId !== null) {
                            return String(v.storeId) === storeIdStr && (Number(v.quantity) || 0) > 0;
                        }
                        if (Array.isArray(v.product_stores) && v.product_stores.length > 0) {
                            const ps = v.product_stores.find(x => String(x.storeId) === storeIdStr);
                            return (ps && (Number(ps.quantity) || 0) > 0);
                        }
                        return false;
                    });
                    return hasVariantInStore || hasStoreInLinks;
                }

                if (product.type === 'toy') {
                    const variants = Array.isArray(product.variants) ? product.variants : [];
                    const hasToyVariantInStore = variants.some(v => {
                        const vStoreId = v.storeId ?? v.store_id ?? null;
                        if (vStoreId !== null) return String(vStoreId) === storeIdStr && (Number(v.quantity) || 0) > 0;
                        return false;
                    });
                    return hasToyVariantInStore || hasStoreInLinks;
                }

                return hasStoreInLinks;
            });
        }

        if (gender && (type === 'shoe' || type === 'clothes')) {
            result = result.filter(product => {
                const productGender = product.gender || (product.shoe && product.shoe.gender) || null;
                return productGender === gender;
            });
        }

        if (selectedSizes && selectedSizes.length > 0 && (type === 'shoe' || type === 'clothes')) {
            const sizesSet = new Set(selectedSizes.map(s => String(s).trim().toLowerCase()));
            result = result.filter(product => {
                if (!Array.isArray(product.variants) || product.variants.length === 0) return false;
                return product.variants.some(v => {
                    let qty = Number(v.quantity) || 0;
                    if (storeId) {
                        const sid = String(storeId);
                        if (Array.isArray(v.product_stores) && v.product_stores.length > 0) {
                            const ps = v.product_stores.find(x => String(x.storeId) === sid);
                            qty = Number(ps?.quantity) || 0;
                        } else if (v.storeId !== undefined && v.storeId !== null) {
                            qty = String(v.storeId) === sid ? (Number(v.quantity) || 0) : 0;
                        } else {
                            qty = 0;
                        }
                    }
                    if (qty <= 0) return false;
                    return sizesSet.has(String(v.size || '').trim().toLowerCase());
                });
            });
        }

        if (productSortBy === 'quantity') {
            result = [...result].sort((a, b) => {
                const qa = getProductQuantity(a);
                const qb = getProductQuantity(b);
                return productSortDirection === 'asc' ? qa - qb : qb - qa;
            });
        } else if (productSortBy === 'rating') {
            result = [...result].sort((a, b) => {
                const ra = typeof a.averageRating === 'number' ? a.averageRating : 0;
                const rb = typeof b.averageRating === 'number' ? b.averageRating : 0;
                return productSortDirection === 'asc' ? ra - rb : rb - ra;
            });
        }

        return result;
    }, [gender, type, selectedSizes, productSortBy, productSortDirection, getCategoryWithChildrenIds, getProductQuantity]);

    const paginateRows = useCallback((rows, pageNum) => {
        const startIdx = (pageNum - 1) * limit;
        setProducts(rows.slice(startIdx, startIdx + limit));
        setTotalCount(rows.length);
    }, [limit]);

    // Сохраняем состояние каталога в sessionStorage, чтобы при возврате назад
    // ничего не сбрасывалось и не зависело от query-параметров.
    useEffect(() => {
        try {
            sessionStorage.setItem(shopStateKey, JSON.stringify({
                page,
                search,
                filters,
                selectedSizes,
                pendingSizes,
                productSortBy,
                productSortDirection
            }));
        } catch {}
    }, [shopStateKey, page, search, filters, selectedSizes, pendingSizes, productSortBy, productSortDirection]);

    useEffect(() => {
        const loadProducts = async () => {
            const requestId = ++lastRequestIdRef.current;
            try {
                setLoadingProducts(true);
                const storeId = filters.store_id || null;
                const categoryId = filters.category_id || null;

                const params = {
                    list: 1,
                    limit: 10000,
                    page: 1,
                    gender,
                    type,
                    store_id: storeId,
                    sort_by: productSortBy,
                    sort_dir: productSortDirection,
                    ...(selectedSizes && selectedSizes.length > 0 && (type === 'shoe' || type === 'clothes')
                        ? { 'size[]': selectedSizes }
                        : {}),
                };

                if (categoryId) {
                    params.categoryIds = [...getCategoryWithChildrenIds(categoryId)].join(',');
                }
                if (search && search.trim() !== '') {
                    params.search = search;
                }

                const data = await fetchProducts(params);
                if (requestId !== lastRequestIdRef.current) {
                    return;
                }

                let rows = data.rows || [];
                if (type) {
                    rows = rows.filter(product => product.type === type);
                }

                rows = applyClientFilters(rows, { storeId, categoryId, searchText: search });
                allFilteredRowsRef.current = rows;
                paginateRows(rows, 1);
                if (page !== 1) {
                    setPage(1);
                }
                setHasLoadedOnce(true);
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                setLoadingProducts(false);
            }
        };

        loadProducts();
    }, [filters.category_id, filters.store_id, limit, gender, type, selectedSizes, user?.store_id, search, productSortBy, productSortDirection, getCategoryWithChildrenIds, applyClientFilters, paginateRows]);

    // Пагинация из кеша — без повторного запроса к API
    useEffect(() => {
        if (!hasLoadedOnce) return;
        paginateRows(allFilteredRowsRef.current, page);
    }, [page, hasLoadedOnce, paginateRows]);

    useEffect(() => {
        document.title = "Каталог товаров";
    }, []);

    const handleSizeSelect = (size) => {
        if (!pendingSizes.includes(size.value)) {
            setPendingSizes([...pendingSizes, size.value]);
        }
        setSizeInput('');
        setShowSizeSuggestions(false);
    };

    const handleRemoveSize = (sizeToRemove) => {
        setPendingSizes(pendingSizes.filter(size => size !== sizeToRemove));
    };

    // Изменение фильтра по полу (gender) через URL-параметр
    const handleGenderChange = (newGender) => {
        const params = new URLSearchParams(location.search);
        if (newGender) {
            params.set('gender', newGender);
        } else {
            params.delete('gender');
        }
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    };

    // Фильтрация категорий по типу товара
    const filteredCategories = React.useMemo(() => {
        if (!type) return categories;
        return categories.filter(cat => cat.type === type);
    }, [categories, type]);

    // Функция для смены сортировки из ProductList
    const handleSortChange = (newSortBy, newSortOrder) => {
        setProductSortBy(newSortBy);
        setProductSortDirection(newSortOrder);
        setPage(1); // сбрасываем на первую страницу при смене сортировки
    };

    // Старт поиска по нажатию кнопки/Enter
    const handleSearchSubmit = (value) => {
        setSearch(value || '');
        setPage(1);
    };

    // Сброс всех фильтров и поиска
    const handleResetFilters = () => {
        setFilters({ category_id: '', store_id: '' });
        setSelectedSizes([]);
        setPendingSizes([]);
        setSizeInput('');
        setSearch('');
        setPage(1);
    };

    const activeCategory = filters.category_id
        ? categories.find(c => String(c.id) === String(filters.category_id)) || null
        : null;
    const activeStore = filters.store_id
        ? stores.find(s => String(s.id) === String(filters.store_id)) || null
        : null;
    const hasActiveFilters =
        !!activeCategory ||
        !!activeStore ||
        !!gender ||
        (Array.isArray(selectedSizes) && selectedSizes.length > 0);

    const prettyType = (val) => {
        if (val === 'shoe') return 'Обувь';
        if (val === 'clothes') return 'Одежда';
        if (val === 'toy') return 'Игрушки';
        return '';
    };

    const prettyGender = (val) => {
        if (val === 'мужской') return 'Для мужчин';
        if (val === 'женский') return 'Для женщин';
        if (val === 'детский') return 'Для детей';
        return '';
    };

    const typeLabel = prettyType(type);
    const genderLabel = prettyGender(gender);

    return (
        <div className="shop-page-wrap">
            <Modal
                show={showFilters}
                onHide={() => setShowFilters(false)}
                centered
                backdrop="static"
                animation={false}
                style={{ pointerEvents: 'auto' }}
                dialogClassName="filter-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Фильтры</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    {/* Магазин */}
                    <Form.Group className="mb-3">
                        <Form.Label>Магазин</Form.Label>
                        <Form.Select 
                            value={filters.store_id || ''} 
                            onChange={e => {
                                const store = stores.find(s => String(s.id) === e.target.value);
                                setFilters(prev => ({ ...prev, store_id: store ? store.id : '' }));
                            }}
                        >
                            <option value="">Все магазины</option>
                            {stores.map(store => (
                                <option key={store.id} value={store.id}>{store.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {/* Пол (для обуви и одежды) */}
                    {(type === 'shoe' || type === 'clothes') && (
                        <Form.Group className="mb-3">
                            <Form.Label>Пол</Form.Label>
                            <Form.Select
                                value={gender || ''}
                                onChange={e => handleGenderChange(e.target.value || '')}
                            >
                                <option value="">Не важно</option>
                                <option value="мужской">Мужская</option>
                                <option value="женский">Женская</option>
                            </Form.Select>
                        </Form.Group>
                    )}

                    {/* Размеры для обуви и одежды */}
                    {(type === 'shoe' || (type === 'clothes' && filters.category_id)) && allSizes.length > 0 && (
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Размеры {(() => {
                                    const cat = categories.find(c => String(c.id) === String(filters.category_id));
                                    return cat ? `(${cat.name})` : '';
                                })()}
                            </Form.Label>
                            <div className="position-relative">
                                <InputGroup>
                                    <FormControl
                                        type="text"
                                        placeholder="Введите размер..."
                                        value={sizeInput}
                                        onChange={(e) => {
                                            setSizeInput(e.target.value);
                                        }}
                                        onClick={() => {
                                            setShowSizeSuggestions(true);
                                            setFilteredSizes(allSizes);
                                        }}
                                        onFocus={() => {
                                            setShowSizeSuggestions(true);
                                            setFilteredSizes(allSizes);
                                        }}
                                    />
                                    <Button 
                                        variant="outline-secondary"
                                        onClick={() => {
                                            setSizeInput('');
                                            setFilteredSizes(allSizes);
                                        }}
                                    >
                                        ✕
                                    </Button>
                                </InputGroup>
                                
                                {/* Выпадающий список размеров */}
                                {showSizeSuggestions && filteredSizes.length > 0 && (
                                    <div 
                                        className="position-absolute w-100 bg-white border rounded mt-1 shadow-sm"
                                        style={{ 
                                            zIndex: 1000, 
                                            maxHeight: '200px', 
                                            overflowY: 'auto',
                                            pointerEvents: 'auto'
                                        }}
                                    >
                                        {filteredSizes.map(size => (
                                            <div
                                                key={size.id}
                                                className="p-2 hover-bg-light cursor-pointer"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleSizeSelect(size)}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                            >
                                                {size.value}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Выбранные размеры */}
                                {pendingSizes.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {pendingSizes.map(size => (
                                            <div 
                                                key={size}
                                                className="bg-light rounded-pill px-3 py-1 d-flex align-items-center"
                                            >
                                                <span>{size}</span>
                                                <Button
                                                    variant="link"
                                                    className="p-0 ms-2 text-danger"
                                                    onClick={() => handleRemoveSize(size)}
                                                    style={{ fontSize: '0.8rem' }}
                                                >
                                                    ✕
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Form.Group>
                    )}

                    {/* Категории */}
                    <Form.Group className="mb-3">
                        <Form.Label>Категория</Form.Label>
                        <Form.Select
                            name="category_id"
                            value={filters.category_id}
                            onChange={e => setFilters(prev => ({ ...prev, category_id: e.target.value }))}
                        >
                            <option value="">Все категории</option>
                            {filteredCategories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setPendingSizes(selectedSizes); // Отмена - сброс к уже примененным
                        setShowFilters(false)
                    }}>
                        Отмена
                    </Button>
                    <Button variant="primary" onClick={() => {
                        setSelectedSizes(pendingSizes); // Применяем выбранные размеры
                        setShowFilters(false)
                    }}>
                        Применить
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className="container-xxl shop-page">
                <div className="shop-hero-card">
                    <div className="shop-hero-text">
                        <p className="eyebrow">Каталог товаров</p>
                        <h1>Подберите товар без лишних усилий</h1>
                        <p className="hero-sub">
                            Удобный поиск, чтобы быстрее найти нужное.
                        </p>
                        <div className="hero-badges">
                            {typeLabel && <span className="hero-pill">{typeLabel}</span>}
                            {genderLabel && <span className="hero-pill muted">{genderLabel}</span>}
                            <span className="hero-pill accent">Всего {totalCount || 0} товаров</span>
                            {hasActiveFilters && <span className="hero-pill success">Фильтры применены</span>}
                        </div>
                    </div>
                    <div className="hero-metrics">
                        <div className="metric-card">
                            <span className="metric-label">В выдаче</span>
                            <strong className="metric-value">{totalCount || 0}</strong>
                            <span className="metric-hint">товаров по выбранным условиям</span>
                        </div>
                        <div className="metric-card">
                            <span className="metric-label">Запрос</span>
                            <strong className="metric-value">{search ? `"${search}"` : 'Все товары'}</strong>
                            <span className="metric-hint">
                                {hasActiveFilters ? 'Уточните фильтры или сбросьте их' : 'Используйте поиск или фильтры'}
                            </span>
                        </div>
                    </div>
                </div>

                <Row className="gy-4">
                    <Col md={3} className="d-none d-md-block">
                        <div className="side-panel">
                            <div className="side-panel__header">
                                <p className="eyebrow">Категории</p>
                                <h5>{typeLabel || 'Вся витрина'}</h5>
                                <span className="side-panel__hint">Быстрый переход по разделам</span>
                            </div>
                            <TypeBar
                                categories={allCategories}
                                selectedCategory={categories.find(c => String(c.id) === String(filters.category_id)) || null}
                                setSelectedCategory={category => setFilters(prev => ({ ...prev, category_id: category.id }))}
                                selectedType={type}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md={9}>
                        <ProductList
                            products={products}
                            page={page}
                            setPage={setPage}
                            totalCount={totalCount}
                            limit={limit}
                            setShowFilters={setShowFilters}
                            search={search}
                            setSearch={setSearch}
                            sortBy={productSortBy}
                            sortOrder={productSortDirection}
                            onSortChange={handleSortChange}
                            onSearchSubmit={handleSearchSubmit}
                            isSearching={loadingProducts && !!search}
                            isLoading={loadingProducts}
                            hasLoadedOnce={hasLoadedOnce}
                            activeFilters={{
                                category: activeCategory,
                                store: activeStore,
                                sizes: selectedSizes,
                                gender
                            }}
                            hasActiveFilters={hasActiveFilters}
                            onResetFilters={handleResetFilters}
                        />
                        <div className="shop-pagination" aria-label="Пагинация">
                            <Pages totalCount={totalCount} page={page} setPage={setPage} limit={limit} />
                        </div>
                    </Col>
                </Row>
            </div>
            <FloatingCartButton />
        </div>
    );
});

export default Shop;